import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  ChevronUpIcon,
  ChevronDownIcon,
  SearchIcon,
} from 'lucide-angular';
import type { Producto, Atributo } from '../../../types/product.types';
import { PriceFilter } from './price-filter';

export interface FilterOption {
  value: string;
  label: string;
  count: number;
  selected: boolean;
}

export interface FilterCategory {
  name: string;
  key: string;
  options: FilterOption[];
  expanded: boolean;
  showAll: boolean;
  searchTerm: string;
}

@Component({
  selector: 'allas-product-filters',
  imports: [CommonModule, FormsModule, LucideAngularModule, PriceFilter],
  template: `
    <!-- Mobile Sort Dropdown -->
    <ng-container *ngTemplateOutlet="mobileSortDropdown"></ng-container>

    <!-- Price Filter -->
    <allas-price-filter
      [products]="products"
      [currentPriceFilters]="currentPriceFilters()"
      (priceFiltersChanged)="onPriceFiltersChanged($event)"
    ></allas-price-filter>

    <!-- Filter Categories -->
    <div class="space-y-4">
      @for (category of dynamicFilterCategories(); track category.key) {
      <ng-container
        *ngTemplateOutlet="filterCategory; context: { $implicit: category }"
      ></ng-container>
      }
    </div>

    <!-- Clear All Filters -->
    @if (hasActiveFilters()) {
    <div class="mt-6 pt-4 border-t border-gray-200">
      <button
        type="button"
        (click)="clearAllFilters()"
        class="w-full text-sm text-gray-600 hover:text-gray-800 underline"
      >
        Limpiar todos los filtros
      </button>
    </div>
    }

    <!-- Templates -->
    <ng-template #mobileSortDropdown>
      <div class="xl:hidden mb-6">
        <label
          for="sort-select"
          class="block text-sm font-medium text-gray-700 mb-2"
        >
          Ordenar por
        </label>
        <select
          id="sort-select"
          [ngModel]="currentSortBy"
          (ngModelChange)="onSortChange($event)"
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="relevance">Destacados</option>
          <option value="price-low">Precio (menor a mayor)</option>
          <option value="price-high">Precio (mayor a menor)</option>
          <option value="rating">Valoración del cliente</option>
          <option value="best-sellers">Más vendidos</option>
        </select>
      </div>
    </ng-template>

    <ng-template #filterCategory let-category>
      <div class="border-b border-gray-100 pb-4 last:border-b-0">
        <!-- Category Header -->
        <button
          type="button"
          class="flex items-center justify-between w-full cursor-pointer border-b-[0.5px] border-gray-700 py-3"
          (click)="toggleCategory(category.key)"
          [attr.aria-expanded]="category.expanded"
          [attr.aria-controls]="'category-' + category.key"
        >
          <span class="font-bold text-gray-900 fs-condensed">
            {{ category.name }}
          </span>
          <lucide-icon
            [name]="category.expanded ? chevronUpIcon : chevronDownIcon"
            class="w-4 h-4 text-gray-500"
            aria-hidden="true"
          ></lucide-icon>
        </button>

        <!-- Filter Options -->
        @if (category.expanded) {
        <div class="mt-3 space-y-3" [id]="'category-' + category.key">
          <!-- Search Input -->
          <ng-container
            *ngTemplateOutlet="searchInput; context: { $implicit: category }"
          ></ng-container>

          <!-- Filter Options List -->
          <ng-container
            *ngTemplateOutlet="
              filterOptionsList;
              context: { $implicit: category }
            "
          ></ng-container>

          <!-- Show More/Less Button -->
          <ng-container
            *ngTemplateOutlet="showMoreButton; context: { $implicit: category }"
          ></ng-container>
        </div>
        }
      </div>
    </ng-template>

    <ng-template #searchInput let-category>
      @if (category.options.length > 5) {
      <div class="relative">
        <lucide-icon
          [name]="searchIcon"
          class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
          aria-hidden="true"
        ></lucide-icon>
        <input
          type="text"
          [id]="'search-' + category.key"
          [placeholder]="'Buscar en ' + category.name.toLowerCase()"
          [value]="category.searchTerm"
          (input)="updateSearchTerm(category.key, $event)"
          class="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent fs-condensed"
        />
      </div>
      }
    </ng-template>

    <ng-template #filterOptionsList let-category>
      @for (option of getVisibleOptions(category); track option.value) {
      <label class="flex items-center space-x-2 cursor-pointer">
        <input
          type="checkbox"
          [id]="'filter-' + category.key + '-' + option.value"
          [checked]="option.selected"
          (change)="toggleFilter(category.key, option.value)"
          class="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-500 fs-condensed"
        />
        <span class="text-sm text-gray-700 fs-condensed">
          {{ option.label }}
        </span>
        <span class="text-xs text-gray-500 fs-condensed">
          ({{ option.count }})
        </span>
      </label>
      } @if (getVisibleOptions(category).length === 0 && category.searchTerm) {
      <p class="text-sm text-gray-500 text-center py-2 fs-condensed">
        No se encontraron resultados para "{{ category.searchTerm }}"
      </p>
      }
    </ng-template>

    <ng-template #showMoreButton let-category>
      @if (category.options.length > 5) {
      <button
        type="button"
        class="text-xs text-blue-600 hover:text-blue-800 underline mt-2 fs-condensed"
        (click)="toggleShowMore(category.key)"
      >
        {{ category.showAll ? 'Ver menos' : 'Ver más' }}
      </button>
      }
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ProductFilters {
  @Input() showCategoryFilters = false;
  @Input() products: Producto[] = [];
  @Input() currentSortBy: string = 'relevance';
  @Output() filtersChanged = new EventEmitter<Record<string, string[]>>();
  @Output() sortChanged = new EventEmitter<string>();
  @Output() priceFiltersChanged = new EventEmitter<{
    min?: number;
    max?: number;
  }>();

  readonly chevronUpIcon = ChevronUpIcon;
  readonly chevronDownIcon = ChevronDownIcon;
  readonly searchIcon = SearchIcon;

  private _filters = signal<Record<string, string[]>>({});
  filters = this._filters.asReadonly();

  private _priceFilters = signal<{ min?: number; max?: number }>({});
  currentPriceFilters = this._priceFilters.asReadonly();

  // Signals para mantener el estado de las categorías dinámicas
  private _categoryStates = signal<
    Record<string, { expanded: boolean; showAll: boolean; searchTerm: string }>
  >({});

  // Computed que genera categorías dinámicamente basadas en atributos y campos del producto
  dynamicFilterCategories = computed(() => {
    const categories: FilterCategory[] = [];

    // Agregar filtros de marca y categoría si showCategoryFilters es true
    const brandCategory = this.getBrandCategory();

    if (brandCategory && brandCategory.options.length > 0) {
      categories.push(brandCategory);
    }

    if (this.showCategoryFilters) {
      const categoryCategory = this.getCategoryCategory();

      if (categoryCategory && categoryCategory.options.length > 0) {
        categories.push(categoryCategory);
      }
    }

    // Agregar categorías de atributos
    const attributeCategories = this.getAttributeCategories();
    categories.push(
      ...attributeCategories.filter((cat) => cat.options.length > 0)
    );

    return categories;
  });

  hasActiveFilters = computed(() => {
    return Object.values(this._filters()).some((values) => values.length > 0);
  });

  constructor() {
    // Efecto para inicializar estados de categorías cuando cambien los productos
    effect(() => {
      const attributeMap = this.buildAttributeMap();
      this.initializeCategoryStates(attributeMap);
    });
  }

  // Método para crear la categoría de marca
  private getBrandCategory(): FilterCategory | null {
    const brands = this.getUniqueBrands();
    if (brands.length === 0) return null;

    const categoryKey = 'marca';
    const state = this.getCategoryState(categoryKey);

    const options = brands.map((brand) => ({
      value: brand,
      label: brand,
      count: this.getCountByBrand(brand),
      selected: this.isBrandSelected(brand),
    }));

    return {
      name: 'Marca',
      key: categoryKey,
      options: options,
      expanded: state.expanded,
      showAll: state.showAll,
      searchTerm: state.searchTerm,
    };
  }

  // Método para crear la categoría de categoría
  private getCategoryCategory(): FilterCategory | null {
    const categories = this.getUniqueCategories();
    if (categories.length === 0) return null;

    const categoryKey = 'categoria';
    const state = this.getCategoryState(categoryKey);

    const options = categories.map((category) => ({
      value: category,
      label: category,
      count: this.getCountByCategory(category),
      selected: this.isCategorySelected(category),
    }));

    return {
      name: 'Categoría',
      key: categoryKey,
      options: options,
      expanded: state.expanded,
      showAll: state.showAll,
      searchTerm: state.searchTerm,
    };
  }

  // Método principal que genera categorías dinámicamente basadas en atributos
  private getAttributeCategories(): FilterCategory[] {
    const attributeMap = this.buildAttributeMap();
    const categories: FilterCategory[] = [];

    // Procesar cada tipo de atributo encontrado
    Object.entries(attributeMap).forEach(([attributeLabel, values]) => {
      const categoryKey = this.sanitizeKey(attributeLabel);
      const state = this.getCategoryState(categoryKey);

      // Filtrar valores vacíos y crear opciones
      const validValues = values.filter(
        (value) =>
          value &&
          value.trim() !== '' &&
          value !== 'null' &&
          value !== 'undefined'
      );

      if (validValues.length > 0) {
        const uniqueValues = [...new Set(validValues)].sort((a, b) => {
          // Ordenar numéricamente si es posible, sino alfabéticamente
          const numA = parseFloat(a);
          const numB = parseFloat(b);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          return a.localeCompare(b);
        });

        const options = uniqueValues.map((value) => ({
          value: value,
          label: value,
          count: this.getCountByAttributeValue(attributeLabel, value),
          selected: this.isAttributeSelected(attributeLabel, value),
        }));

        categories.push({
          name: attributeLabel,
          key: categoryKey,
          options: options,
          expanded: state.expanded,
          showAll: state.showAll,
          searchTerm: state.searchTerm,
        });
      }
    });

    return categories;
  }

  // Obtiene marcas únicas de los productos
  private getUniqueBrands(): string[] {
    const brands = this.products
      .map((product) => product.marca)
      .filter((brand) => brand && brand.trim() !== '');

    return [...new Set(brands)].sort((a, b) => a.localeCompare(b));
  }

  // Obtiene categorías únicas de los productos
  private getUniqueCategories(): string[] {
    const categories = this.products
      .map((product) => product.categoria)
      .filter((category) => category && category.trim() !== '');

    return [...new Set(categories)].sort((a, b) => a.localeCompare(b));
  }

  // Obtiene el conteo de productos por marca
  private getCountByBrand(brand: string): number {
    const filteredProducts = this.getFilteredProductsByBrand();
    return filteredProducts.filter((product) => product.marca === brand).length;
  }

  // Obtiene el conteo de productos por categoría
  private getCountByCategory(category: string): number {
    const filteredProducts = this.getFilteredProductsByCategory();
    return filteredProducts.filter((product) => product.categoria === category)
      .length;
  }

  // Verifica si una marca está seleccionada
  private isBrandSelected(brand: string): boolean {
    return this._filters()['marca']?.includes(brand) || false;
  }

  // Verifica si una categoría está seleccionada
  private isCategorySelected(category: string): boolean {
    return this._filters()['categoria']?.includes(category) || false;
  }

  // Obtiene productos filtrados por otros filtros (excluyendo marca)
  private getFilteredProductsByBrand(): Producto[] {
    const activeFilters = this._filters();
    let filteredProducts = [...this.products];

    Object.entries(activeFilters).forEach(([filterKey, filterValues]) => {
      if (filterValues.length > 0 && filterKey !== 'marca') {
        filteredProducts = filteredProducts.filter((product) => {
          return this.productMatchesFilter(product, filterKey, filterValues);
        });
      }
    });

    return filteredProducts;
  }

  // Obtiene productos filtrados por otros filtros (excluyendo categoría)
  private getFilteredProductsByCategory(): Producto[] {
    const activeFilters = this._filters();
    let filteredProducts = [...this.products];

    Object.entries(activeFilters).forEach(([filterKey, filterValues]) => {
      if (filterValues.length > 0 && filterKey !== 'categoria') {
        filteredProducts = filteredProducts.filter((product) => {
          return this.productMatchesFilter(product, filterKey, filterValues);
        });
      }
    });

    return filteredProducts;
  }

  // Verifica si un producto cumple con un filtro específico
  private productMatchesFilter(
    product: Producto,
    filterKey: string,
    filterValues: string[]
  ): boolean {
    // Filtros de marca y categoría
    if (filterKey === 'marca') {
      return filterValues.includes(product.marca);
    }
    if (filterKey === 'categoria') {
      return filterValues.includes(product.categoria);
    }

    // Filtros de atributos
    return this.productMatchesAttributeFilter(product, filterKey, filterValues);
  }

  // Construye un mapa de todos los atributos únicos encontrados en los productos
  private buildAttributeMap(): Record<string, string[]> {
    const attributeMap: Record<string, string[]> = {};

    this.products.forEach((product) => {
      if (product.atributos && Array.isArray(product.atributos)) {
        product.atributos
          .filter((atr) => atr.filterable)
          .forEach((atributo) => {
            if (atributo.label && atributo.value) {
              if (!attributeMap[atributo.label]) {
                attributeMap[atributo.label] = [];
              }
              attributeMap[atributo.label].push(String(atributo.value).trim());
            }
          });
      }
    });

    return attributeMap;
  }

  // Sanitiza el label del atributo para crear una key única
  private sanitizeKey(label: string): string {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }

  // Inicializa los estados de categorías que no existen
  private initializeCategoryStates(
    attributeMap: Record<string, string[]>
  ): void {
    const currentStates = { ...this._categoryStates() };
    let hasChanges = false;

    // Inicializar estado para filtro de marca (siempre disponible)
    if (!currentStates['marca']) {
      currentStates['marca'] = {
        expanded: true,
        showAll: false,
        searchTerm: '',
      };
      hasChanges = true;
    }

    // Inicializar estado para filtro de categoría si está habilitado
    if (this.showCategoryFilters) {
      if (!currentStates['categoria']) {
        currentStates['categoria'] = {
          expanded: true,
          showAll: false,
          searchTerm: '',
        };
        hasChanges = true;
      }
    }

    // Inicializar estados para atributos
    Object.keys(attributeMap).forEach((attributeLabel) => {
      const categoryKey = this.sanitizeKey(attributeLabel);
      if (!currentStates[categoryKey]) {
        currentStates[categoryKey] = {
          expanded: true,
          showAll: false,
          searchTerm: '',
        };
        hasChanges = true;
      }
    });

    if (hasChanges) {
      this._categoryStates.set(currentStates);
    }
  }

  // Obtiene el estado de una categoría (sin modificar signals)
  private getCategoryState(categoryKey: string): {
    expanded: boolean;
    showAll: boolean;
    searchTerm: string;
  } {
    const currentStates = this._categoryStates();
    return (
      currentStates[categoryKey] || {
        expanded: true,
        showAll: false,
        searchTerm: '',
      }
    );
  }

  // Obtiene el conteo de productos que tienen un atributo específico con un valor específico
  private getCountByAttributeValue(
    attributeLabel: string,
    value: string
  ): number {
    const filteredProducts =
      this.getFilteredProductsByAttributes(attributeLabel);
    return filteredProducts.filter((product) => {
      return this.productHasAttributeValue(product, attributeLabel, value);
    }).length;
  }

  // Verifica si un producto tiene un atributo específico con un valor específico
  private productHasAttributeValue(
    product: Producto,
    attributeLabel: string,
    value: string
  ): boolean {
    if (!product.atributos || !Array.isArray(product.atributos)) {
      return false;
    }

    return product.atributos.some(
      (atributo) =>
        atributo.label === attributeLabel &&
        String(atributo.value).trim() === value.trim()
    );
  }

  // Obtiene productos filtrados por otros atributos (excluyendo el atributo actual)
  private getFilteredProductsByAttributes(
    excludeAttributeLabel?: string
  ): Producto[] {
    const activeFilters = this._filters();
    let filteredProducts = [...this.products];

    // Aplicar todos los filtros activos excepto el atributo excluido
    Object.entries(activeFilters).forEach(([filterKey, filterValues]) => {
      if (filterValues.length > 0) {
        // Verificar si este filtro corresponde al atributo excluido
        const isExcludedAttribute = this.isAttributeFilter(
          filterKey,
          excludeAttributeLabel
        );
        if (!isExcludedAttribute) {
          filteredProducts = filteredProducts.filter((product) => {
            return this.productMatchesAttributeFilter(
              product,
              filterKey,
              filterValues
            );
          });
        }
      }
    });

    return filteredProducts;
  }

  // Verifica si un filtro corresponde a un atributo específico
  private isAttributeFilter(
    filterKey: string,
    attributeLabel?: string
  ): boolean {
    if (!attributeLabel) return false;
    const sanitizedAttributeKey = this.sanitizeKey(attributeLabel);
    return filterKey === sanitizedAttributeKey;
  }

  // Verifica si un producto cumple con un filtro de atributo
  private productMatchesAttributeFilter(
    product: Producto,
    filterKey: string,
    filterValues: string[]
  ): boolean {
    if (!product.atributos || !Array.isArray(product.atributos)) {
      return false;
    }

    // Buscar el atributo que corresponde a este filtro
    const matchingAttribute = product.atributos.find(
      (atributo) => this.sanitizeKey(atributo.label) === filterKey
    );

    if (!matchingAttribute) {
      return false;
    }

    const productValue = String(matchingAttribute.value).trim();
    return filterValues.some(
      (filterValue) => productValue === filterValue.trim()
    );
  }

  // Verifica si un atributo está seleccionado en los filtros
  private isAttributeSelected(attributeLabel: string, value: string): boolean {
    const filterKey = this.sanitizeKey(attributeLabel);
    return this._filters()[filterKey]?.includes(value) || false;
  }

  getVisibleOptions(category: FilterCategory): FilterOption[] {
    let filteredOptions = category.options;

    // Aplicar filtro de búsqueda si hay término de búsqueda
    if (category.searchTerm.trim()) {
      filteredOptions = category.options.filter((option) =>
        option.label.toLowerCase().includes(category.searchTerm.toLowerCase())
      );
    }

    // Ordenar: seleccionados primero, luego por tipo de campo
    filteredOptions = filteredOptions.sort((a, b) => {
      // Si uno está seleccionado y el otro no, el seleccionado va primero
      if (a.selected && !b.selected) return -1;
      if (!a.selected && b.selected) return 1;

      // Si ambos tienen el mismo estado de selección, ordenar según el tipo de campo
      const isNumericField = this.isNumericField(category.key);
      if (isNumericField) {
        // Ordenar numéricamente para campos numéricos
        const numA = parseFloat(a.label);
        const numB = parseFloat(b.label);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
      }

      // Ordenar alfabéticamente para campos de texto o como fallback
      return a.label.localeCompare(b.label);
    });

    // Si hay búsqueda, mostrar todos los resultados ordenados
    if (category.searchTerm.trim()) {
      return filteredOptions;
    }

    // Si no hay búsqueda, aplicar límite de opciones visibles
    if (category.showAll || filteredOptions.length <= 5) {
      return filteredOptions;
    }
    return filteredOptions.slice(0, 5);
  }

  private isNumericField(fieldKey: string): boolean {
    // Verificar si el campo contiene valores numéricos comunes
    const numericPatterns = [
      'capacidad',
      'voltaje',
      'amperaje',
      'volumen',
      'peso',
      'dimension',
      'temperatura',
      'presion',
      'velocidad',
      'rpm',
      'frecuencia',
      'potencia',
    ];

    return numericPatterns.some((pattern) =>
      fieldKey.toLowerCase().includes(pattern)
    );
  }

  updateSearchTerm(categoryKey: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const currentStates = { ...this._categoryStates() };
    const state = currentStates[categoryKey];

    if (state) {
      state.searchTerm = input.value;
      if (input.value.trim()) {
        state.showAll = true;
      }
      this._categoryStates.set(currentStates);
    }
  }

  onSearchInput(categoryKey: string): void {
    const currentStates = { ...this._categoryStates() };
    const state = currentStates[categoryKey];

    if (state && state.searchTerm.trim()) {
      state.showAll = true;
      this._categoryStates.set(currentStates);
    }
  }

  toggleCategory(key: string): void {
    const currentStates = { ...this._categoryStates() };
    const state = currentStates[key];

    if (state) {
      state.expanded = !state.expanded;
      this._categoryStates.set(currentStates);
    }
  }

  toggleShowMore(key: string): void {
    const currentStates = { ...this._categoryStates() };
    const state = currentStates[key];

    if (state) {
      state.showAll = !state.showAll;
      this._categoryStates.set(currentStates);
    }
  }

  toggleFilter(categoryKey: string, value: string): void {
    const currentFilters = { ...this._filters() };

    if (!currentFilters[categoryKey]) {
      currentFilters[categoryKey] = [];
    }

    const index = currentFilters[categoryKey].indexOf(value);
    if (index > -1) {
      currentFilters[categoryKey].splice(index, 1);
    } else {
      currentFilters[categoryKey].push(value);
    }

    this._filters.set(currentFilters);
    this.clearSearchTerms(); // Limpiar términos de búsqueda cuando cambien los filtros
    this.filtersChanged.emit(currentFilters);
  }

  private clearSearchTerms(): void {
    const currentStates = { ...this._categoryStates() };

    Object.keys(currentStates).forEach((key) => {
      if (currentStates[key]) {
        currentStates[key].searchTerm = '';
        // NO resetear showAll - debe mantenerse persistente
      }
    });

    this._categoryStates.set(currentStates);
  }

  clearAllFilters(): void {
    this._filters.set({});
    this._priceFilters.set({});
    this.clearSearchTerms();
    this.filtersChanged.emit({});
    this.priceFiltersChanged.emit({});
  }

  onSortChange(value: string): void {
    this.sortChanged.emit(value);
  }

  onPriceFiltersChanged(priceFilters: { min?: number; max?: number }): void {
    this._priceFilters.set(priceFilters);
    this.priceFiltersChanged.emit(priceFilters);
  }
}
